import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Toggle from '../Toggle/Toggle';
import { resolveImageUrl } from '../../utils/imageUrl';
import useNavigationPrompt from '../../hooks/useNavigationPrompt';
import { useLanguage } from '../../contexts/LanguageContext';
import type { Category, BranchDto, ServiceDetailsResponse, AgeGroup, ServiceOptionGroupDto } from '../../types/api-responses';
import { generateUniqueServiceName } from './generateUniqueServiceName';
import { formatWireDuration } from '../../lib/humanDuration';
import { translateValidationMessage } from '../../lib/ownerValidationMessages';
import styles from './ServiceAddEditModal.module.css';
// PBI 1962 (AB#1968) — flexible option-groups builder (flag-gated, default OFF).
import ServiceOptionGroupsBuilder from '../ServiceOptionGroupsBuilder/ServiceOptionGroupsBuilder';
import { isServiceOptionGroupsEnabled } from '../../lib/serviceOptionGroups/serviceOptionGroupsFlag';
import type { OptionGroupModel, OptionGroupWirePayload } from '../../types/serviceOptionGroups';
import {
    dtoToModel,
    durationStringToMinutes,
    makeDefaultVariantGroup,
    modelToWirePayload,
    modelToFlatPricingOptions,
    validateOptionGroups,
} from '../../lib/serviceOptionGroups/serviceOptionGroups';

/**
 * CR-2026-05-12-v2 — Service add/edit modal (canonical single-modal pattern).
 *
 * Replaces the standalone /services 3-step wizard for the My Business shell
 * entry point. Single page, dark Fresha tokens, RHF + Zod, no staff
 * assignment (deferred to a follow-up PBI). Existing assignedStaffIds are
 * threaded through unchanged on save so dropping the staff step here doesn't
 * wipe assignments set previously via the standalone wizard.
 *
 * Currency is hardcoded to AED for v1 — currency-per-business inheritance
 * arrives with the marketplace pricing PBI.
 */

const SERVICE_TYPE = {
    MEN: 1,
    WOMEN: 2,
    EVERYONE: 3,
} as const;

// O3 (SO-38) — labels come from formatWireDuration(value, t) at render time
// ("1 h 30 min" / «ساعة و30 دقيقة»).
const DURATION_OPTIONS: Array<{ value: string }> = [
    { value: '00:15:00' },
    { value: '00:30:00' },
    { value: '00:45:00' },
    { value: '01:00:00' },
    { value: '01:15:00' },
    { value: '01:30:00' },
    { value: '02:00:00' },
    { value: '02:30:00' },
    { value: '03:00:00' },
];

/**
 * O3 (SO-27) — a name still carrying the auto-added copy suffix, in either
 * language: "Blow-dry (copy)", "Blow-dry (copy 2)", «تسريحة (نسخة)».
 */
export function hasCopySuffix(name: string): boolean {
    return /\((?:copy|نسخة)(?:\s+\d+)?\)\s*$/i.test(name.trim());
}

/** Names of services in one (category, branch) scope — the BE uniqueness scope. */
function namesInScope(
    services: ReadonlyArray<ServiceDetailsResponse>,
    categoryName: string | undefined,
    branchId: number | undefined,
): string[] {
    return services
        .filter(s => s.categoryName === categoryName && s.branchId === branchId)
        .map(s => s.name);
}

const DEFAULT_CURRENCY = 'AED';
const MAX_IMAGE_BYTES = 5 * 1024 * 1024; // 5 MB

/* ── Pricing tier sub-schema ──────────────────────────────────────── */
const pricingOptionSchema = z.object({
    name: z
        .string()
        .min(1, 'Tier name is required')
        .max(100, 'Tier name cannot exceed 100 characters'),
    price: z.number({ invalid_type_error: 'Price is required' }).min(0, 'Price must be ≥ 0'),
    currency: z.string().min(1).max(3),
    duration: z.string().min(1, 'Duration is required'),
});

/* ── Modal form schema (image handled outside RHF) ────────────────── */
const formSchema = z.object({
    name: z
        .string()
        .min(2, 'Service name must be at least 2 characters')
        .max(100, 'Service name cannot exceed 100 characters'),
    // O3 (SO-25) — optional Arabic name / description shown to Arabic customers.
    nameAr: z.string().max(100, 'Service name cannot exceed 100 characters').optional().or(z.literal('')),
    descriptionAr: z.string().max(1000, 'Description cannot exceed 1000 characters').optional().or(z.literal('')),
    branchId: z.number({ invalid_type_error: 'Branch is required' }).positive('Branch is required'),
    categoryId: z.number().min(0),
    minDuration: z.string().min(1, 'Duration is required'),
    maxDuration: z.string().min(1, 'Duration is required'),
    serviceType: z.number().int().min(1).max(3),
    // AB#1847 (PBI-I) / AB#1838 — Age axis of the gender matrix. REQUIRED;
    // the owner must explicitly pick Adult / Kid / Both (no implicit default
    // in the form). The form stores `'Adult' | 'Kid' | 'Both' | ''` so the
    // empty-string sentinel maps to "unchosen" — refine rejects it with the
    // same field-error UX as the other required pickers.
    ageGroup: z
        .union([z.literal(''), z.literal('Adult'), z.literal('Kid'), z.literal('Both')])
        .refine(v => v === 'Adult' || v === 'Kid' || v === 'Both', {
            message: 'Please pick who this service is for',
        }),
    hasHomeService: z.boolean(),
    isActive: z.boolean(),
    description: z
        .string()
        .max(1000, 'Description cannot exceed 1000 characters')
        .optional()
        .or(z.literal('')),
    pricingOptions: z
        .array(pricingOptionSchema)
        .min(1, 'At least one pricing tier is required'),
});

// AB#1847 — Use `z.input` (pre-refine) so the form value type for ageGroup
// includes the empty-string "unchosen" sentinel. After validation succeeds
// the value is narrowed to `'Adult' | 'Kid' | 'Both'` in `onValid`.
type ServiceForm = z.input<typeof formSchema>;

/** External-facing data shape — parent constructs this and reads it back on save. */
export interface ServiceFormData {
    categoryId: number;
    branchId: number;
    name: string;
    /** O3 (SO-25) — optional Arabic name / description. */
    nameAr?: string;
    descriptionAr?: string;
    image: File | null;
    removeExistingImage: boolean;
    minDuration: string;
    maxDuration: string;
    serviceType: number;
    /**
     * AB#1847 (PBI-I) / AB#1838 — Age axis of the gender matrix. Required
     * on create — the parent factory (`emptyModalData`) initialises this
     * to `null` so the modal forces an explicit pick before submit. On
     * edit (`rowToModalData`) the BE row is back-filled to "Adult" if
     * missing, so this is always non-null at render time.
     */
    ageGroup: AgeGroup | null;
    hasHomeService: boolean;
    description: string;
    isActive: boolean;
    pricingOptions: Array<{
        name: string;
        price: number;
        currency: string;
        duration: string;
    }>;
    /**
     * PBI 1962 (AB#1968) — flexible option groups (variants & add-ons), as the
     * serialized `OptionGroupsJson` wire payload. Populated by the modal ONLY
     * when the option-groups builder flag is ON; otherwise omitted so the
     * backend auto-wraps the flat `pricingOptions` (exact legacy path). The
     * parent forwards it verbatim to createService/updateService.
     */
    optionGroups?: OptionGroupWirePayload[];
    /**
     * PBI 1962 (AB#1968) — existing option-group DTOs read from the service
     * (edit mode) used to SEED the builder. Read-only input; not sent back
     * (the modal sends the serialized `optionGroups` above instead).
     */
    existingOptionGroups?: ServiceOptionGroupDto[];
    /** Pre-existing staff assignments, threaded through untouched on save. */
    preservedStaffIds: number[];
}

export interface ServiceAddEditModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: ServiceFormData) => void | Promise<void>;
    initialData: ServiceFormData;
    isAdd: boolean;
    loading?: boolean;
    onDirtyChange?: (dirty: boolean) => void;
    onDeleteClicked?: () => void;
    categories: Category[];
    branches: BranchDto[];
    existingImageUrl?: string;
    /**
     * γ-lite (B6') — every existing service across the business. When set
     * and the modal is in Add mode, a "Copy from another service" picker
     * appears at the top of the form. Picking pre-fills name, durations,
     * service type, home-service flag, description, pricing tiers, and
     * category — but NOT branch (the owner still chooses the target
     * branch) and NOT the image (each branch typically wants its own
     * imagery, and File objects don't survive a copy operation cleanly).
     *
     * The new row that gets saved is fully independent — editing it later
     * never affects the original. This is a deliberate UX time-saver
     * without taking a stance on cross-branch templating (see γ-full
     * discussion in audit doc / chat).
     */
    existingServices?: ServiceDetailsResponse[];
}

const ServiceAddEditModal: React.FC<ServiceAddEditModalProps> = ({
    isOpen,
    onClose,
    onSave,
    initialData,
    isAdd,
    loading = false,
    onDirtyChange,
    onDeleteClicked,
    categories,
    branches,
    existingImageUrl,
    existingServices,
}) => {
    const { t, language } = useLanguage();
    const errText = (m: string | undefined) => translateValidationMessage(m, t);
    const nameInputRef = useRef<HTMLInputElement | null>(null);
    // O3 (SO-27) — the service the form was pre-filled from and the name we
    // generated for it, so a branch / category change can re-check the name.
    const [copyInfo, setCopyInfo] = useState<{ sourceName: string; autoName: string } | null>(null);
    const [copyNameError, setCopyNameError] = useState<string | null>(null);

    const {
        control,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors, isDirty },
    } = useForm<ServiceForm>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            name: initialData.name,
            nameAr: initialData.nameAr ?? '',
            descriptionAr: initialData.descriptionAr ?? '',
            branchId: initialData.branchId || 0,
            categoryId: initialData.categoryId || 0,
            minDuration: initialData.minDuration,
            maxDuration: initialData.maxDuration,
            serviceType: initialData.serviceType,
            // AB#1847 (PBI-I) — empty sentinel = "unchosen". On Add the
            // factory passes null → '' here; on Edit the BE row populates
            // 'Adult' / 'Kid' / 'Both' directly.
            ageGroup: initialData.ageGroup ?? '',
            hasHomeService: initialData.hasHomeService,
            isActive: initialData.isActive,
            description: initialData.description,
            pricingOptions: initialData.pricingOptions,
        },
    });

    // Image is managed outside RHF (File objects don't serialize cleanly).
    const [image, setImage] = useState<File | null>(null);
    const [removeExistingImage, setRemoveExistingImage] = useState(false);
    const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
    const [imageError, setImageError] = useState<string | null>(null);
    // O3 (SO-38) — a stored image that fails to load shows the placeholder.
    const [existingImageBroken, setExistingImageBroken] = useState(false);
    useEffect(() => { setExistingImageBroken(false); }, [existingImageUrl]);

    /* ── PBI 1962 (AB#1968) — option-groups builder (flag-gated) ─────── */
    // Feature flag defaults OFF → the classic flat pricing-tiers UI renders and
    // only PricingOptionsJson is sent (ships DARK). When ON, the builder
    // replaces the flat list, is seeded from existing groups (or the flat
    // options), managed outside RHF, and serialized on submit.
    const optionGroupsEnabled = useMemo(() => isServiceOptionGroupsEnabled(), []);
    const [optionGroups, setOptionGroups] = useState<OptionGroupModel[]>([]);
    const [optionGroupsTouched, setOptionGroupsTouched] = useState(false);

    const watchedPricing = watch('pricingOptions');
    const watchedName = watch('name');
    const watchedBranchId = watch('branchId');
    const watchedCategoryId = watch('categoryId');
    const watchedMin = watch('minDuration');
    const watchedMax = watch('maxDuration');

    /* ── Image preview lifecycle ────────────────────────────────────── */
    useEffect(() => {
        if (!image) {
            setImagePreviewUrl(null);
            return;
        }
        const url = URL.createObjectURL(image);
        setImagePreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [image]);

    /* ── Reset form when modal opens with fresh initialData ─────────── */
    useEffect(() => {
        if (!isOpen) return;
        reset({
            name: initialData.name,
            nameAr: initialData.nameAr ?? '',
            descriptionAr: initialData.descriptionAr ?? '',
            branchId: initialData.branchId || 0,
            categoryId: initialData.categoryId || 0,
            minDuration: initialData.minDuration,
            maxDuration: initialData.maxDuration,
            serviceType: initialData.serviceType,
            ageGroup: initialData.ageGroup ?? '',
            hasHomeService: initialData.hasHomeService,
            isActive: initialData.isActive,
            description: initialData.description,
            pricingOptions: initialData.pricingOptions,
        });
        setImage(null);
        setRemoveExistingImage(false);
        setImageError(null);
        setCopyInfo(null);
        setCopyNameError(null);
        // PBI 1962 — seed the option-groups builder. Existing groups (edit mode)
        // map via dtoToModel and carry the legacy auto-wrap badge; otherwise we
        // wrap the flat pricing options into a default Single/1..1 Variant group
        // so legacy + net-new services start uniform (Faris §10.C).
        if (optionGroupsEnabled) {
            const existing = initialData.existingOptionGroups;
            if (existing && existing.length > 0) {
                setOptionGroups(dtoToModel(existing));
            } else {
                setOptionGroups([
                    makeDefaultVariantGroup(
                        initialData.pricingOptions.map(p => ({
                            name: p.name,
                            price: p.price,
                            durationMin: durationStringToMinutes(p.duration),
                        })),
                    ),
                ]);
            }
        } else {
            setOptionGroups([]);
        }
        setOptionGroupsTouched(false);
        if (onDirtyChange) onDirtyChange(false);
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    /* ── Notify parent of dirty state ───────────────────────────────── */
    useEffect(() => {
        if (!onDirtyChange) return;
        const imageChanged = image !== null || removeExistingImage;
        onDirtyChange(isDirty || imageChanged);
    }, [isDirty, image, removeExistingImage, onDirtyChange]);

    /* ── PBI 1962 — validate option groups (mirrors backend rules) ──── */
    const optionGroupsValidation = useMemo(
        () => validateOptionGroups(optionGroups),
        [optionGroups],
    );

    const handleOptionGroupsChange = useCallback((next: OptionGroupModel[]) => {
        setOptionGroups(next);
        setOptionGroupsTouched(true);
        if (onDirtyChange) onDirtyChange(true);
    }, [onDirtyChange]);

    /**
     * O3 (SO-27) — the name a copy should get at the CURRENT target branch +
     * category: the source name as-is when it's free there (copying to another
     * branch keeps the customer-facing name), otherwise the "(copy)" suggestion,
     * which the owner must replace before saving.
     */
    const copyNameFor = useCallback((sourceName: string, branchId: number, categoryId: number) => {
        const categoryName = categories.find(c => c.id === categoryId)?.name;
        const taken = namesInScope(existingServices ?? [], categoryName, branchId)
            .map(n => n.trim().toLowerCase());
        if (!taken.includes(sourceName.trim().toLowerCase())) return sourceName.trim();
        return generateUniqueServiceName(
            sourceName,
            namesInScope(existingServices ?? [], categoryName, branchId),
            t('services.copyFrom.suffix.first'),
            (n: number) => t('services.copyFrom.suffix.nth').replace('{n}', String(n)),
        );
    }, [categories, existingServices, t]);

    // Re-check an untouched copy name when the owner changes branch / category.
    useEffect(() => {
        if (!copyInfo || !isAdd) return;
        if (watchedName !== copyInfo.autoName) return;
        const next = copyNameFor(copyInfo.sourceName, Number(watchedBranchId) || 0, Number(watchedCategoryId) || 0);
        if (next !== watchedName) {
            setValue('name', next, { shouldDirty: true });
            setCopyInfo({ sourceName: copyInfo.sourceName, autoName: next });
        }
    }, [watchedBranchId, watchedCategoryId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Clear the copy-name error as soon as the owner edits the name.
    useEffect(() => {
        if (copyNameError) setCopyNameError(null);
    }, [watchedName]); // eslint-disable-line react-hooks/exhaustive-deps

    /* ── Validate min ≤ max ─────────────────────────────────────────── */
    const durationConflict = useMemo(() => {
        const minIdx = DURATION_OPTIONS.findIndex(o => o.value === watchedMin);
        const maxIdx = DURATION_OPTIONS.findIndex(o => o.value === watchedMax);
        if (minIdx < 0 || maxIdx < 0) return null;
        if (minIdx > maxIdx) return t('mb.serviceForm.durationConflict');
        return null;
    }, [watchedMin, watchedMax, t]);

    /* ── Image handlers ─────────────────────────────────────────────── */
    const handleImagePick = (file: File | null) => {
        setImageError(null);
        if (!file) {
            setImage(null);
            return;
        }
        if (!file.type.startsWith('image/')) {
            setImageError(t('mb.serviceForm.image.badType'));
            return;
        }
        if (file.size > MAX_IMAGE_BYTES) {
            setImageError(t('mb.serviceForm.image.tooLarge'));
            return;
        }
        setImage(file);
        setRemoveExistingImage(false);
    };

    const handleRemoveImage = () => {
        setImage(null);
        setRemoveExistingImage(true);
        setImageError(null);
    };

    /* ── Pricing tier helpers ───────────────────────────────────────── */
    const addPricingTier = () => {
        setValue(
            'pricingOptions',
            [
                ...watchedPricing,
                { name: '', price: 0, currency: DEFAULT_CURRENCY, duration: watchedMin },
            ],
            { shouldDirty: true },
        );
    };

    const removePricingTier = (index: number) => {
        if (watchedPricing.length <= 1) return;
        setValue(
            'pricingOptions',
            watchedPricing.filter((_, i) => i !== index),
            { shouldDirty: true },
        );
    };

    /* ── Submit ─────────────────────────────────────────────────────── */
    const onValid = (data: ServiceForm) => {
        if (durationConflict) return;
        // O3 (SO-27) — a new service may not be saved with the auto "(copy)"
        // suffix (customers would see it), nor with a name that already exists
        // at the same branch in the same category (the BE would reject it).
        if (isAdd) {
            const trimmed = data.name.trim();
            const categoryName = categories.find(c => c.id === data.categoryId)?.name;
            const clash = namesInScope(existingServices ?? [], categoryName, data.branchId)
                .some(n => n.trim().toLowerCase() === trimmed.toLowerCase());
            if (hasCopySuffix(trimmed) || clash) {
                setCopyNameError(t(clash ? 'mb.serviceForm.nameTaken' : 'mb.serviceForm.renameCopy'));
                nameInputRef.current?.focus();
                nameInputRef.current?.select();
                return;
            }
        }
        // PBI 1962 — when the builder is ON, block save on invalid groups and
        // surface the error banner (backend re-validates regardless).
        if (optionGroupsEnabled && !optionGroupsValidation.ok) {
            setOptionGroupsTouched(true);
            return;
        }
        // AB#1847 — `ageGroup` passes Zod refine only as the closed enum,
        // so this narrow is safe at this point. The cast is a documentation
        // assert rather than a behavioural change.
        const ageGroup = data.ageGroup as AgeGroup;

        // PBI 1962 — when ON, the builder is authoritative: serialize
        // OptionGroupsJson AND derive the flat PricingOptionsJson fallback from
        // the Variant group (dual-write — keeps old readers + the Appointment
        // scalar FK satisfied). When OFF, send the untouched flat options only.
        const currency = data.pricingOptions[0]?.currency ?? DEFAULT_CURRENCY;
        const optionGroupsPayload: OptionGroupWirePayload[] | undefined =
            optionGroupsEnabled ? modelToWirePayload(optionGroups, currency) : undefined;
        const pricingOptions =
            optionGroupsEnabled
                ? modelToFlatPricingOptions(optionGroups, currency)
                : data.pricingOptions;

        const result: ServiceFormData = {
            // B16 — In Edit mode, BE now accepts CategoryId and re-parents
            // when it differs from the current. Always pass data.categoryId
            // so the owner can move a mis-categorized service in-place.
            categoryId: data.categoryId,
            branchId: data.branchId,
            name: data.name.trim(),
            nameAr: (data.nameAr ?? '').trim(),
            descriptionAr: (data.descriptionAr ?? '').trim(),
            image,
            removeExistingImage,
            minDuration: data.minDuration,
            maxDuration: data.maxDuration,
            serviceType: data.serviceType,
            ageGroup,
            hasHomeService: data.hasHomeService,
            description: data.description ?? '',
            isActive: data.isActive,
            pricingOptions,
            optionGroups: optionGroupsPayload,
            preservedStaffIds: initialData.preservedStaffIds ?? [],
        };
        onSave(result);
    };

    const handleClose = useCallback(() => {
        const imageChanged = image !== null || removeExistingImage;
        if (isDirty || imageChanged) {
            const ok = window.confirm(t('mb.common.discardChanges'));
            if (!ok) return;
        }
        onClose();
    }, [isDirty, image, removeExistingImage, onClose, t]);

    useNavigationPrompt(
        isOpen && (isDirty || image !== null || removeExistingImage),
        t('mb.common.discardChanges'),
    );

    /* ── Render ─────────────────────────────────────────────────────── */
    return (
        <div className={`${styles.modalBackground} ${isOpen ? styles.open : ''}`}>
            <div className={`${styles.modalContainer} ${isOpen ? styles.open : ''}`}>
                {loading && (
                    <div className={styles.loadingOverlay}>
                        <div className={styles.loadingContent}>
                            <div className={styles.spinner} />
                            <span>{isAdd ? t('mb.serviceForm.creating') : t('mb.serviceForm.saving')}</span>
                        </div>
                    </div>
                )}

                <div className={styles.headerCloseInputsContainer}>
                    <button
                        type="button"
                        className={styles.closeButton}
                        onClick={handleClose}
                        disabled={loading}
                        aria-label={t('mb.common.close')}
                    >
                        ×
                    </button>
                    <div>
                        <h2 className={styles.header}>
                            {isAdd ? t('mb.serviceForm.addTitle') : t('mb.serviceForm.editTitle')}
                        </h2>
                        <p className={styles.subheader}>{t('mb.serviceForm.subtitle')}</p>
                    </div>

                    {/* γ-lite — "Copy from another service" picker.
                        Only in Add mode + when there's at least one existing
                        service in the business. Picking copies name + durations
                        + service type + home-service flag + description +
                        pricing tiers + category into the form below, then the
                        owner edits whatever needs to differ for this branch.
                        The new row is saved as fully independent — editing
                        it later never affects the original. */}
                    {isAdd && existingServices && existingServices.length > 0 && (
                        <div className={styles.copyFromSection}>
                            <label htmlFor="service-copy-from" className={styles.sectionLabel}>
                                {t('mb.serviceForm.copyFrom')} <span className={styles.optionalHint}>{t('mb.serviceForm.copyFromHint')}</span>
                            </label>
                            <select
                                id="service-copy-from"
                                className={styles.copyFromSelect}
                                defaultValue=""
                                disabled={loading}
                                onChange={(e) => {
                                    const sourceId = parseInt(e.target.value, 10);
                                    if (!sourceId) return;
                                    const src = existingServices.find(s => s.id === sourceId);
                                    if (!src) return;
                                    // AB#1899 / O3 (SO-27) — name for the TARGET scope
                                    // (current branch + the source's category): the
                                    // source name when it's free there (copying to
                                    // another branch keeps the customer-facing name);
                                    // otherwise the "(copy)" suggestion, which must be
                                    // renamed before save (onValid blocks it).
                                    const sourceCat = categories.find(c => c.name === src.categoryName);
                                    const targetCategoryId = sourceCat?.id ?? (Number(watchedCategoryId) || 0);
                                    const suggestedName = copyNameFor(
                                        src.name,
                                        Number(watchedBranchId) || 0,
                                        targetCategoryId,
                                    );
                                    setCopyInfo({ sourceName: src.name, autoName: suggestedName });
                                    // Pre-fill RHF fields. Branch is left untouched —
                                    // owner chooses where to add this copy. Image is
                                    // never copied (File objects don't transfer; each
                                    // branch typically wants its own imagery).
                                    setValue('name', suggestedName, { shouldDirty: true });
                                    setValue('minDuration', src.minDuration, { shouldDirty: true });
                                    setValue('maxDuration', src.maxDuration, { shouldDirty: true });
                                    setValue('serviceType', typeof src.serviceType === 'number'
                                        ? src.serviceType
                                        : src.serviceType === 'MenOnly' ? 1
                                            : src.serviceType === 'WomenOnly' ? 2
                                                : 3,
                                        { shouldDirty: true });
                                    setValue('hasHomeService', !!src.hasHomeService, { shouldDirty: true });
                                    setValue('description', src.description ?? '', { shouldDirty: true });
                                    // O3 (SO-25) — copy keeps the Arabic text (no suffix on it).
                                    setValue('nameAr', src.nameAr ?? '', { shouldDirty: true });
                                    setValue('descriptionAr', src.descriptionAr ?? '', { shouldDirty: true });
                                    // B16 — BE now accepts CategoryId on edit too,
                                    // so this lookup works in both Add and Edit modes.
                                    // Find by name (most reliable cross-branch lookup
                                    // since IDs are business-scoped).
                                    if (sourceCat) {
                                        setValue('categoryId', sourceCat.id, { shouldDirty: true });
                                    }
                                    if (src.pricingOptions && src.pricingOptions.length > 0) {
                                        setValue('pricingOptions', src.pricingOptions.map(p => ({
                                            name: p.name,
                                            price: p.price,
                                            currency: p.currency,
                                            duration: p.duration,
                                        })), { shouldDirty: true });
                                    }
                                    // Reset the picker so the same selection can be
                                    // re-applied (rare, but possible if the owner
                                    // edited fields and wants to start fresh).
                                    e.target.value = '';
                                    // A same-branch duplicate needs a new name: put the
                                    // cursor there with the text selected.
                                    if (hasCopySuffix(suggestedName)) {
                                        setTimeout(() => {
                                            nameInputRef.current?.focus();
                                            nameInputRef.current?.select();
                                        }, 0);
                                    }
                                }}
                            >
                                <option value="">{t('mb.serviceForm.copyFromPlaceholder')}</option>
                                {existingServices.map(svc => {
                                    const branchName = branches.find(b => b.id === svc.branchId)?.name ?? t('mb.serviceForm.unknownBranch');
                                    return (
                                        <option key={svc.id} value={svc.id}>
                                            {svc.name} · {branchName}
                                        </option>
                                    );
                                })}
                            </select>
                            <p className={styles.copyFromHint}>{t('mb.serviceForm.copyFromHelp')}</p>
                        </div>
                    )}

                    {/* Image uploader */}
                    <div>
                        <label className={styles.sectionLabel}>{t('mb.serviceForm.photo')}</label>
                        <div className={styles.imageRow}>
                            <label className={styles.imageDrop}>
                                {imagePreviewUrl ? (
                                    <img src={imagePreviewUrl} alt="" />
                                ) : !removeExistingImage && existingImageUrl && !existingImageBroken ? (
                                    <img
                                        src={resolveImageUrl(existingImageUrl)}
                                        alt=""
                                        onError={() => setExistingImageBroken(true)}
                                    />
                                ) : (
                                    <>
                                        <span className={styles.imageDropIcon} aria-hidden="true">📷</span>
                                        <span>{t('mb.common.upload')}</span>
                                    </>
                                )}
                                <input
                                    type="file"
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                    onChange={e => handleImagePick(e.target.files?.[0] ?? null)}
                                    disabled={loading}
                                />
                            </label>
                            <div className={styles.imageActions}>
                                <span className={styles.imageHint}>
                                    {t('mb.serviceForm.photoHint')}
                                </span>
                                {(image ||
                                    (!removeExistingImage && existingImageUrl)) && (
                                    <button
                                        type="button"
                                        className={styles.removeImageBtn}
                                        onClick={handleRemoveImage}
                                        disabled={loading}
                                    >
                                        {t('mb.serviceForm.removeImage')}
                                    </button>
                                )}
                                {imageError && (
                                    <div className={styles.fieldError}>{imageError}</div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Service name */}
                    <Controller
                        name="name"
                        control={control}
                        render={({ field }) => (
                            <div className={styles.field}>
                                <label htmlFor="service-name">{t('mb.serviceForm.name')}</label>
                                <input
                                    ref={(el) => {
                                        field.ref(el);
                                        nameInputRef.current = el;
                                    }}
                                    id="service-name"
                                    type="text"
                                    placeholder={t('mb.serviceForm.namePlaceholder')}
                                    value={field.value}
                                    onChange={e => field.onChange(e.target.value)}
                                    disabled={loading}
                                    maxLength={100}
                                    aria-invalid={errors.name || copyNameError ? true : undefined}
                                    aria-describedby={copyNameError ? 'service-name-copy-error' : undefined}
                                />
                                {errors.name && (
                                    <div className={styles.fieldError}>{errText(errors.name.message)}</div>
                                )}
                                {copyNameError && !errors.name && (
                                    <div id="service-name-copy-error" className={styles.fieldError} role="alert">
                                        {copyNameError}
                                    </div>
                                )}
                                {!copyNameError && isAdd && hasCopySuffix(field.value ?? '') && (
                                    <div className={styles.copyFromHint}>{t('mb.serviceForm.renameCopyHint')}</div>
                                )}
                            </div>
                        )}
                    />

                    {/* B16 — Category dropdown rendered in BOTH Add and Edit
                        modes. BE's UpdateServiceCommand now accepts an optional
                        CategoryId and re-parents the service when it differs
                        from the current one. Owners can fix a mis-categorized
                        service in-place without delete + re-add. */}
                    <Controller
                        name="categoryId"
                        control={control}
                        render={({ field }) => (
                            <div className={styles.field}>
                                <label htmlFor="service-category">{t('mb.services.filter.category')}</label>
                                <select
                                    id="service-category"
                                    value={field.value || ''}
                                    onChange={e =>
                                        field.onChange(parseInt(e.target.value, 10) || 0)
                                    }
                                    disabled={loading}
                                >
                                    <option value="">{t('mb.serviceForm.selectCategory')}</option>
                                    {categories.map(cat => (
                                        <option key={cat.id} value={cat.id}>
                                            {language === 'ar' && cat.nameAr ? cat.nameAr : cat.name}
                                        </option>
                                    ))}
                                </select>
                                {field.value === 0 && errors.categoryId && (
                                    <div className={styles.fieldError}>{t('mb.serviceForm.categoryRequired')}</div>
                                )}
                            </div>
                        )}
                    />


                    {/* Branch */}
                    <Controller
                        name="branchId"
                        control={control}
                        render={({ field }) => (
                            <div className={styles.field}>
                                <label htmlFor="service-branch">{t('mb.serviceForm.branch')}</label>
                                <select
                                    id="service-branch"
                                    value={field.value || ''}
                                    onChange={e =>
                                        field.onChange(parseInt(e.target.value, 10) || 0)
                                    }
                                    disabled={loading}
                                >
                                    <option value="">{t('mb.serviceForm.selectBranch')}</option>
                                    {branches.map(b => (
                                        <option key={b.id} value={b.id}>
                                            {b.name}
                                        </option>
                                    ))}
                                </select>
                                {errors.branchId && (
                                    <div className={styles.fieldError}>{errText(errors.branchId.message)}</div>
                                )}
                            </div>
                        )}
                    />

                    {/* Service type — chip-button group */}
                    <Controller
                        name="serviceType"
                        control={control}
                        render={({ field }) => (
                            <div>
                                <label className={styles.sectionLabel}>{t('mb.serviceForm.serviceType')}</label>
                                <div className={styles.chipGroup}>
                                    <button
                                        type="button"
                                        className={`${styles.chip} ${field.value === SERVICE_TYPE.EVERYONE ? styles.chipActive : ''}`}
                                        onClick={() => field.onChange(SERVICE_TYPE.EVERYONE)}
                                        disabled={loading}
                                    >
                                        {t('mb.serviceForm.type.everyone')}
                                    </button>
                                    <button
                                        type="button"
                                        className={`${styles.chip} ${field.value === SERVICE_TYPE.WOMEN ? styles.chipActive : ''}`}
                                        onClick={() => field.onChange(SERVICE_TYPE.WOMEN)}
                                        disabled={loading}
                                    >
                                        {t('mb.serviceForm.type.women')}
                                    </button>
                                    <button
                                        type="button"
                                        className={`${styles.chip} ${field.value === SERVICE_TYPE.MEN ? styles.chipActive : ''}`}
                                        onClick={() => field.onChange(SERVICE_TYPE.MEN)}
                                        disabled={loading}
                                    >
                                        {t('mb.serviceForm.type.men')}
                                    </button>
                                </div>
                            </div>
                        )}
                    />

                    {/* AB#1847 (PBI-I) / AB#1838 — Age-group axis of the
                        gender matrix. REQUIRED on Add/Edit; the owner must
                        explicitly pick Adult / Kid / Both. AR `Both` =
                        `كلاهما` per founder Q1 (NOT `الجميع`, which is the
                        gender-axis `Everyone` label). Bilingual via t(). */}
                    <Controller
                        name="ageGroup"
                        control={control}
                        render={({ field }) => (
                            <div>
                                <label className={styles.sectionLabel}>
                                    {t('services.ageGroup.label')}
                                </label>
                                <div
                                    className={styles.chipGroup}
                                    role="radiogroup"
                                    aria-label={t('services.ageGroup.label')}
                                    aria-required="true"
                                >
                                    <button
                                        type="button"
                                        className={`${styles.chip} ${field.value === 'Adult' ? styles.chipActive : ''}`}
                                        onClick={() => field.onChange('Adult')}
                                        disabled={loading}
                                        aria-checked={field.value === 'Adult'}
                                        role="radio"
                                    >
                                        {t('services.ageGroup.adults')}
                                    </button>
                                    <button
                                        type="button"
                                        className={`${styles.chip} ${field.value === 'Kid' ? styles.chipActive : ''}`}
                                        onClick={() => field.onChange('Kid')}
                                        disabled={loading}
                                        aria-checked={field.value === 'Kid'}
                                        role="radio"
                                    >
                                        {t('services.ageGroup.kids')}
                                    </button>
                                    <button
                                        type="button"
                                        className={`${styles.chip} ${field.value === 'Both' ? styles.chipActive : ''}`}
                                        onClick={() => field.onChange('Both')}
                                        disabled={loading}
                                        aria-checked={field.value === 'Both'}
                                        role="radio"
                                    >
                                        {t('services.ageGroup.both')}
                                    </button>
                                </div>
                                {errors.ageGroup && (
                                    <div className={styles.fieldError} role="alert">
                                        {t('services.ageGroup.required')}
                                    </div>
                                )}
                            </div>
                        )}
                    />

                    {/* Min / Max duration */}
                    <div className={styles.row}>
                        <Controller
                            name="minDuration"
                            control={control}
                            render={({ field }) => (
                                <div className={styles.field}>
                                    <label htmlFor="service-min-duration">{t('mb.serviceForm.minDuration')}</label>
                                    <select
                                        id="service-min-duration"
                                        value={field.value}
                                        onChange={e => field.onChange(e.target.value)}
                                        disabled={loading}
                                    >
                                        {DURATION_OPTIONS.map(opt => (
                                            <option key={opt.value} value={opt.value}>
                                                {formatWireDuration(opt.value, t)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        />
                        <Controller
                            name="maxDuration"
                            control={control}
                            render={({ field }) => (
                                <div className={styles.field}>
                                    <label htmlFor="service-max-duration">{t('mb.serviceForm.maxDuration')}</label>
                                    <select
                                        id="service-max-duration"
                                        value={field.value}
                                        onChange={e => field.onChange(e.target.value)}
                                        disabled={loading}
                                    >
                                        {DURATION_OPTIONS.map(opt => (
                                            <option key={opt.value} value={opt.value}>
                                                {formatWireDuration(opt.value, t)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        />
                    </div>
                    {durationConflict && (
                        <div className={styles.fieldError}>{durationConflict}</div>
                    )}

                    {/* PBI 1962 (AB#1968) — option-groups builder (flag ON) OR
                        the legacy flat pricing-tiers list (flag OFF, default). */}
                    {optionGroupsEnabled ? (
                        <div>
                            <label className={styles.sectionLabel}>
                                {t('services.builder.title')}
                            </label>
                            <ServiceOptionGroupsBuilder
                                value={optionGroups}
                                onChange={handleOptionGroupsChange}
                                currency={watchedPricing[0]?.currency ?? DEFAULT_CURRENCY}
                                disabled={loading}
                                errorKeys={
                                    optionGroupsTouched ? optionGroupsValidation.errorKeys : []
                                }
                            />
                        </div>
                    ) : (
                    /* Pricing tiers */
                    <div>
                        <label className={styles.sectionLabel}>
                            {t('mb.serviceForm.tiers').replace('{n}', String(watchedPricing.length))}
                        </label>
                        <div className={styles.pricingList}>
                            {watchedPricing.map((tier, index) => (
                                <div key={index} className={styles.pricingRow}>
                                    <div className={styles.pricingRowHeader}>
                                        <span className={styles.pricingRowLabel}>
                                            {t('mb.serviceForm.tier').replace('{n}', String(index + 1))}
                                        </span>
                                        {watchedPricing.length > 1 && (
                                            <button
                                                type="button"
                                                className={styles.removeTierBtn}
                                                onClick={() => removePricingTier(index)}
                                                disabled={loading}
                                            >
                                                {t('mb.common.remove')}
                                            </button>
                                        )}
                                    </div>
                                    <div className={styles.pricingRowGrid}>
                                        <Controller
                                            name={`pricingOptions.${index}.name` as const}
                                            control={control}
                                            render={({ field }) => (
                                                <div className={styles.field}>
                                                    <label>{t('mb.serviceForm.tierName')}</label>
                                                    <input
                                                        type="text"
                                                        aria-label={t('mb.serviceForm.tierName')}
                                                        placeholder={t('mb.serviceForm.tierNamePlaceholder')}
                                                        value={field.value}
                                                        onChange={e => field.onChange(e.target.value)}
                                                        disabled={loading}
                                                        maxLength={100}
                                                    />
                                                    {errors.pricingOptions?.[index]?.name && (
                                                        <div className={styles.fieldError}>
                                                            {errText(errors.pricingOptions[index]?.name?.message)}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        />
                                        <Controller
                                            name={`pricingOptions.${index}.duration` as const}
                                            control={control}
                                            render={({ field }) => (
                                                <div className={styles.field}>
                                                    <label>{t('mb.serviceForm.duration')}</label>
                                                    <select
                                                        aria-label={t('mb.serviceForm.duration')}
                                                        value={field.value}
                                                        onChange={e => field.onChange(e.target.value)}
                                                        disabled={loading}
                                                    >
                                                        {DURATION_OPTIONS.map(opt => (
                                                            <option key={opt.value} value={opt.value}>
                                                                {formatWireDuration(opt.value, t)}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            )}
                                        />
                                        <Controller
                                            name={`pricingOptions.${index}.price` as const}
                                            control={control}
                                            render={({ field }) => (
                                                <div className={styles.field}>
                                                    <label>{t('mb.serviceForm.price')}</label>
                                                    <div className={styles.priceInputWrap}>
                                                        <input
                                                            aria-label={t('mb.serviceForm.price')}
                                                            type="number"
                                                            inputMode="decimal"
                                                            min="0"
                                                            step="0.01"
                                                            placeholder="0"
                                                            value={field.value || ''}
                                                            onChange={e => {
                                                                const v = parseFloat(e.target.value);
                                                                field.onChange(isNaN(v) ? 0 : v);
                                                            }}
                                                            disabled={loading}
                                                        />
                                                        <span className={styles.priceCurrency}>
                                                            {watchedPricing[index]?.currency ?? DEFAULT_CURRENCY}
                                                        </span>
                                                    </div>
                                                    {errors.pricingOptions?.[index]?.price && (
                                                        <div className={styles.fieldError}>
                                                            {errText(errors.pricingOptions[index]?.price?.message)}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                        <button
                            type="button"
                            className={styles.addTierBtn}
                            onClick={addPricingTier}
                            disabled={loading}
                            style={{ marginTop: 10 }}
                        >
                            {t('mb.serviceForm.addTier')}
                        </button>
                        {errors.pricingOptions && typeof errors.pricingOptions.message === 'string' && (
                            <div className={styles.fieldError} style={{ marginTop: 6 }}>
                                {errText(errors.pricingOptions.message)}
                            </div>
                        )}
                    </div>
                    )}

                    {/* Description */}
                    <Controller
                        name="description"
                        control={control}
                        render={({ field }) => (
                            <div className={styles.field}>
                                <label htmlFor="service-description">{t('mb.serviceForm.description')}</label>
                                <textarea
                                    id="service-description"
                                    placeholder={t('mb.serviceForm.descriptionPlaceholder')}
                                    value={field.value ?? ''}
                                    onChange={e => field.onChange(e.target.value)}
                                    disabled={loading}
                                    maxLength={1000}
                                />
                                {errors.description && (
                                    <div className={styles.fieldError}>{errText(errors.description.message)}</div>
                                )}
                            </div>
                        )}
                    />

                    {/* O3 (SO-25) — Arabic name + description */}
                    <Controller
                        name="nameAr"
                        control={control}
                        render={({ field }) => (
                            <div className={styles.field}>
                                <label htmlFor="service-name-ar">{t('mb.serviceForm.nameAr')}</label>
                                <input
                                    id="service-name-ar"
                                    type="text"
                                    dir="rtl"
                                    placeholder={t('mb.serviceForm.nameArPlaceholder')}
                                    value={field.value ?? ''}
                                    onChange={e => field.onChange(e.target.value)}
                                    disabled={loading}
                                    maxLength={100}
                                    aria-describedby="service-name-ar-hint"
                                />
                                <div id="service-name-ar-hint" className={styles.fieldHint}>{t('mb.serviceForm.nameArHint')}</div>
                                {errors.nameAr && (
                                    <div className={styles.fieldError}>{errText(errors.nameAr.message)}</div>
                                )}
                            </div>
                        )}
                    />
                    <Controller
                        name="descriptionAr"
                        control={control}
                        render={({ field }) => (
                            <div className={styles.field}>
                                <label htmlFor="service-description-ar">{t('mb.serviceForm.descriptionAr')}</label>
                                <textarea
                                    id="service-description-ar"
                                    dir="rtl"
                                    placeholder={t('mb.serviceForm.descriptionArPlaceholder')}
                                    value={field.value ?? ''}
                                    onChange={e => field.onChange(e.target.value)}
                                    disabled={loading}
                                    maxLength={1000}
                                />
                                {errors.descriptionAr && (
                                    <div className={styles.fieldError}>{errText(errors.descriptionAr.message)}</div>
                                )}
                            </div>
                        )}
                    />

                    {/* Settings card — active + home service */}
                    <div className={styles.settingsCard}>
                        <div className={styles.settingsCardTitle}>{t('mb.serviceForm.settings')}</div>
                        <div className={styles.settingsCardSubtitle}>{t('mb.serviceForm.settingsSub')}</div>

                        {!isAdd && (
                            <div className={styles.settingsRow}>
                                <div className={styles.settingsRowText}>
                                    <div className={styles.settingsRowLabel}>{t('mb.common.active')}</div>
                                    <div className={styles.settingsRowHelp}>{t('mb.serviceForm.activeHelp')}</div>
                                </div>
                                <div className={styles.settingsRowControl}>
                                    <Controller
                                        name="isActive"
                                        control={control}
                                        render={({ field }) => (
                                            <Toggle
                                                checked={field.value}
                                                onChange={field.onChange}
                                                disabled={loading}
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                        )}

                        <div className={styles.settingsRow}>
                            <div className={styles.settingsRowText}>
                                <div className={styles.settingsRowLabel}>{t('mb.serviceForm.home')}</div>
                                <div className={styles.settingsRowHelp}>{t('mb.serviceForm.homeHelp')}</div>
                            </div>
                            <div className={styles.settingsRowControl}>
                                <Controller
                                    name="hasHomeService"
                                    control={control}
                                    render={({ field }) => (
                                        <Toggle
                                            checked={field.value}
                                            onChange={field.onChange}
                                            disabled={loading}
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className={styles.buttonContainer}>
                    <div className={styles.deleteSlot}>
                        {!isAdd && onDeleteClicked && (
                            <button
                                type="button"
                                className={`${styles.btn} ${styles.btnDanger}`}
                                onClick={onDeleteClicked}
                                disabled={loading}
                            >
                                {t('mb.serviceForm.delete')}
                            </button>
                        )}
                    </div>
                    <div className={styles.cancelAddSave}>
                        <button
                            type="button"
                            className={`${styles.btn} ${styles.btnGhost}`}
                            onClick={handleClose}
                            disabled={loading}
                        >
                            {t('mb.common.cancel')}
                        </button>
                        <button
                            type="button"
                            className={`${styles.btn} ${styles.btnPrimary}`}
                            onClick={handleSubmit(onValid)}
                            disabled={loading || !!durationConflict}
                        >
                            {isAdd ? t('mb.common.add') : t('mb.common.save')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ServiceAddEditModal;
